import { forgotPassword } from '@/lib/auth-client';
import { routes } from '@/lib/routes';
import { CheckCircle, Loader2, Mail } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center space-y-4">
        <div className="mx-auto w-12 h-12 bg-green/10 rounded-full flex items-center justify-center">
          <CheckCircle className="w-6 h-6 text-green" />
        </div>
        <h2 className="text-2xl font-medium text-ink">{t('auth.resetLinkSent')}</h2>
        <p className="text-sepia">{t('auth.resetLinkSentDesc')}</p>
        <Link to={routes.login} className="inline-block mt-4 text-blue hover:underline font-medium">
          {t('auth.backToSignIn')}
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-medium text-ink">{t('auth.forgotPassword')}</h2>
        <p className="text-sepia mt-2">{t('auth.forgotPasswordDesc')}</p>
      </div>

      {error && (
        <div className="bg-red/10 border border-red/20 text-red rounded-md p-3 text-sm">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-sepia mb-1.5">
          {t('auth.email')}
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full pl-10 pr-4 py-2.5 border border-gray-warm rounded-md focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
            placeholder="you@example.com"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 bg-ink text-cream rounded-md font-medium hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('common.loading')}
          </>
        ) : (
          t('auth.sendResetLink')
        )}
      </button>

      <p className="text-center text-sm text-sepia">
        {t('auth.rememberPassword')}{' '}
        <Link to={routes.login} className="text-blue hover:underline font-medium">
          {t('auth.signIn')}
        </Link>
      </p>
    </form>
  );
}
